/**
 * The browser's half of a company's Google connection.
 *
 * What the browser holds, and for how long:
 *
 *  - **Nothing durable.** The refresh token never reaches it. The one access
 *    token it ever sees is the Picker's, fetched just before the Picker opens,
 *    held only by Google's own Picker code, and short-lived (08 §14.1).
 *  - **A note of the connection it started**, in `sessionStorage`, keyed by the
 *    state Google will send back: which company and which screen. The return
 *    page (`/connect/google`) finishes only a connection this tab started —
 *    the server independently refuses a state finished by anybody other than
 *    its starter (0030), so the note is a courtesy and the server is the rule.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AmountModel, ColumnMap, IdentityStrategy } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { apiPost } from '../lib/api';

export type GoogleSheetKind = 'receivables' | 'stock';

export interface GoogleStatus {
    connected: boolean;
    account: string;
    connectedAt: string | null;
    needsReconnect: boolean;
    connectedByName: string;
    connectedByMe: boolean;
}

const NOT_CONNECTED: GoogleStatus = {
    connected: false, account: '', connectedAt: null, needsReconnect: false, connectedByName: '', connectedByMe: false,
};

interface StatusRow {
    connected: boolean;
    account_label: string | null;
    connected_at: string | null;
    needs_reconnect: boolean;
    connected_by_name: string | null;
    connected_by_me: boolean;
}

/** This company's Google connection, as the screen may know it. No credential is in it. */
export function useGoogleConnection(workspaceId: string) {
    const [status, setStatus] = useState<GoogleStatus>(NOT_CONNECTED);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        setError(null);
        let data: unknown = null;
        let failed: unknown = null;
        try {
            ({ data, error: failed } = await supabase().rpc('google_connection_status', { p_workspace: workspaceId }));
        } catch (thrown) {
            // A network failure throws rather than answering; it must still end
            // in a message, not a screen that waits for ever.
            failed = thrown;
        }
        if (issuedFor.current !== workspaceId) return;
        if (failed) {
            setError('We could not check the Google connection. Try again in a moment.');
        } else {
            const row = (data as StatusRow[] | null)?.[0];
            setStatus(row ? {
                connected: Boolean(row.connected),
                account: row.account_label ?? '',
                connectedAt: row.connected_at,
                needsReconnect: Boolean(row.needs_reconnect),
                connectedByName: row.connected_by_name ?? '',
                connectedByMe: Boolean(row.connected_by_me),
            } : NOT_CONNECTED);
        }
        setLoading(false);
    }, [workspaceId]);

    useEffect(() => { void load(); }, [load]);
    return { status, loading, error, reload: load };
}

/* --------------------------- the connection in flight ------------------------ */

const PENDING = 'timely.google.pending.';
/** Longer than the server's ten minutes, so the server's "expired" is what a person sees. */
const PENDING_MAX_AGE_MS = 15 * 60_000;

export interface PendingConnection {
    workspaceId: string;
    slug: string;
    kind: GoogleSheetKind;
    startedAt: number;
}

const storage = (): Storage | null => {
    try { return window.sessionStorage; } catch { return null; }
};

export function rememberPending(state: string, pending: PendingConnection): void {
    try { storage()?.setItem(PENDING + state, JSON.stringify(pending)); } catch { /* a tab with no storage cannot finish; it is told so */ }
}

/** The note for this state, once: reading it removes it. */
export function takePending(state: string, now = Date.now()): PendingConnection | null {
    let raw: string | null = null;
    try {
        raw = storage()?.getItem(PENDING + state) ?? null;
        storage()?.removeItem(PENDING + state);
    } catch {
        return null;
    }
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as PendingConnection;
        const kindOk = parsed.kind === 'receivables' || parsed.kind === 'stock';
        if (!kindOk || typeof parsed.workspaceId !== 'string' || typeof parsed.slug !== 'string') return null;
        if (!(now - parsed.startedAt >= 0 && now - parsed.startedAt < PENDING_MAX_AGE_MS)) return null;
        return parsed;
    } catch {
        return null;
    }
}

/* ---------------------------------- the calls -------------------------------- */

const GOOGLE_CONSENT = 'https://accounts.google.com/';

/**
 * Start connecting, and go to Google. The address comes from our server and
 * is followed only if it is Google's own consent screen.
 */
export async function startGoogleConnection(
    workspaceId: string, slug: string, kind: GoogleSheetKind,
    go: (url: string) => void = url => window.location.assign(url),
): Promise<void> {
    const { url } = await apiPost<{ url: string }>('/api/datasource/google/start', { workspaceId, kind });
    let state: string | null = null;
    try {
        const parsed = new URL(url);
        state = parsed.searchParams.get('state');
        if (!url.startsWith(GOOGLE_CONSENT)) state = null;
    } catch {
        state = null;
    }
    if (!state) throw new Error('The server did not send us to Google. Try again in a moment.');
    rememberPending(state, { workspaceId, slug, kind, startedAt: Date.now() });
    go(url);
}

export const finishGoogleConnection = (workspaceId: string, code: string, state: string) =>
    apiPost<{ kind: GoogleSheetKind; account: string }>('/api/datasource/google/finish', { workspaceId, code, state });

export interface PickerConfig {
    accessToken: string;
    expiresIn: number;
    developerKey: string;
    appId: string;
}

export const pickerConfig = (workspaceId: string) =>
    apiPost<PickerConfig>('/api/datasource/google/picker', { workspaceId });

export interface GoogleTab { sheetId: number; title: string; rowCount: number; columnCount: number }

export interface GooglePreview {
    spreadsheetTitle: string;
    tabs: GoogleTab[];
    tab?: {
        sheetId: number;
        title: string;
        headers: string[];
        sample: string[][];
        rowCount: number;
        suggested: ColumnMap;
        suggestedModel: AmountModel;
    };
}

export const previewGoogleSheet = (workspaceId: string, spreadsheetId: string, sheetId?: number) =>
    apiPost<GooglePreview>('/api/datasource/google/preview',
        sheetId === undefined ? { workspaceId, spreadsheetId } : { workspaceId, spreadsheetId, sheetId });

export interface GoogleChoice {
    spreadsheetId: string;
    sheetId: number;
    columns: Record<string, string>;
    identityStrategy?: IdentityStrategy;
    amountModel?: AmountModel;
}

export const selectGoogleSheet = (workspaceId: string, kind: GoogleSheetKind, choice: GoogleChoice) =>
    apiPost<{ saved: true }>('/api/datasource/google/select', { workspaceId, kind, ...choice });

export const disconnectGoogle = (workspaceId: string) =>
    apiPost<{ disconnected: true }>('/api/datasource/google/disconnect', { workspaceId });
